// Horizontal and vertical bars, plain or stacked. Four callers: the install
// arrivals series (stacked, vertical), the look-up leaderboard, the section
// funnel and the impact radius (all horizontal).
//
// `tone` is a PROP and is never derived from the value — `MeterBar.tsx`'s rule,
// for its reason: which end of a scale is bad depends on the metric. A wide
// impact radius is a fact about coupling, not a failure.
//
// A `null` value is a GAP: the row draws no bar at all, and the caller is
// expected to have printed the backend's reason beside it. It is NOT a zero,
// and a bar of length zero would be the exact fabrication the whole screen
// avoids — see `Figure`.

import { Figure } from "./Figure";
import { band, linear, maxOf } from "./scale";
import "./charts.css";

export type BarTone = "accent" | "nominal" | "flagged" | "danger" | "muted";

export type Bar = {
  key: string;
  label: string;
  /** `null` renders no bar. Never coerce it to 0. */
  value: number | null;
  tone?: BarTone;
  /** Drawn inside the track behind `value` — the funnel's exit share. */
  overlay?: number | null;
};

export type BarsProps = {
  label: string;
  bars: Bar[];
  /** Shared ceiling. Omit to scale to the tallest measured bar. */
  max?: number;
  width?: number;
  /** Per-bar thickness. Total height follows from the bar count. */
  thickness?: number;
  notMeasured?: { reason: string };
};

const GAP = 6;

export function Bars({
  label,
  bars,
  max,
  width = 320,
  thickness = 18,
  notMeasured,
}: BarsProps) {
  const top = max ?? maxOf(bars.map((bar) => bar.value));
  const scale = linear(top, width);
  const height = Math.max(thickness, bars.length * (thickness + GAP) - GAP);
  const slots = band(bars.length, height + GAP, GAP);

  return (
    <Figure label={label} width={width} height={height} notMeasured={notMeasured}>
      {bars.map((bar, index) => (
        <g key={bar.key}>
          <rect
            className="maisight-chart-track"
            x={0}
            y={slots.start(index)}
            width={width}
            height={thickness}
            rx={3}
          />
          {/* A gap draws no fill. The row's own copy says why it is absent. */}
          {bar.value === null ? null : (
            <rect
              className="maisight-chart-bar"
              data-tone={bar.tone ?? "accent"}
              x={0}
              y={slots.start(index)}
              width={Math.max(0, scale(bar.value))}
              height={thickness}
              rx={3}
            />
          )}
          {bar.overlay === null || bar.overlay === undefined ? null : (
            <rect
              className="maisight-chart-bar-overlay"
              x={0}
              y={slots.start(index)}
              width={Math.max(0, scale(bar.overlay))}
              height={thickness}
              rx={3}
            />
          )}
        </g>
      ))}
    </Figure>
  );
}

export type StackSlice = { key: string; value: number };
export type StackColumn = { key: string; label: string; slices: StackSlice[] };

export type StackedBarsProps = {
  label: string;
  columns: StackColumn[];
  /** Draw order, bottom first. Also fixes colour assignment, so a series keeps
   *  its colour between refreshes instead of following dictionary order. */
  keys: string[];
  width?: number;
  height?: number;
  notMeasured?: { reason: string };
};

export function StackedBars({
  label,
  columns,
  keys,
  width = 560,
  height = 140,
  notMeasured,
}: StackedBarsProps) {
  const totals = columns.map((column) =>
    column.slices.reduce((sum, slice) => sum + slice.value, 0),
  );
  const scale = linear(maxOf(totals), height);
  const slots = band(columns.length, width, columns.length > 60 ? 0 : 2);

  return (
    <Figure label={label} width={width} height={height} notMeasured={notMeasured}>
      {columns.map((column, index) => {
        let cursor = height;
        return (
          <g key={column.key}>
            {keys.map((key, order) => {
              const value =
                column.slices.find((slice) => slice.key === key)?.value ?? 0;
              if (value <= 0) return null;
              const size = scale(value);
              cursor -= size;
              return (
                <rect
                  key={key}
                  className="maisight-chart-bar"
                  data-series={order % 5}
                  x={slots.start(index)}
                  y={cursor}
                  width={slots.width}
                  height={size}
                />
              );
            })}
          </g>
        );
      })}
    </Figure>
  );
}
